// PL モバイル page (/m/a-pl)
// - 主要 KPI 12 件 = PC 版のサマリーカードと同じ並び (収益 / 利益 / 費用 の 3 群 × 4)
// - 年計指数 sparkline (基準月=100)
// - 材料費内訳 リスト (医薬品 / 診療材料 / 給食 / 医療消耗器具)
//
// [2026-09-11] PC 版に合わせて 経費等 の作り直しと 医業外損益 の独立に対応した。
//   ★以前は 4 件 (医業収益計/経常利益/材料費計/人件費計) だけで、 しかも
//     `derivePlSeries` を通していなかったので 経費等 は旧式 (研究費・研修費・本部経費 落ち) のままだった。
//     表示していなかったので実害は無かったが、 PC 版と別の定義を抱えたままなのは危ない。

import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { MobileShell } from "../components/MobileShell";
import { Sparkline } from "../components/Sparkline";
import { fmtInt } from "../../lib/format";
import { derivePlSeries, KEIHI_LABEL, NONOP_METRIC } from "../../lib/plBasis";
import type { PlResponse } from "@shared/api";

// 千円 → 百万円 (大規模 PL 用)
const toMillion = (v: number) => v / 1000;
const fmtMillion = (v: number) => `${toMillion(v).toLocaleString("ja-JP", { maximumFractionDigits: 0 })}`;

// ★metric 名は (前期比較).xls 側 = fact_pl_monthly / rolling12 の呼び名を使う。
//   PC 版は (予算比較).xls 側の「入院収益 / 外来収益」 を主に見るが、 モバイルは
//   monthly / rolling12 しか使わないので「入院収入 / 外来収入」 が正しい。
const MOBILE_GROUPS: { label: string; metrics: string[] }[] = [
  { label: "収益", metrics: ["医業収益計", "入院収入", "外来収入", "保険等査定減"] },
  { label: "利益", metrics: ["経常利益", "医業利益", NONOP_METRIC, "経費等"] },
  { label: "費用", metrics: ["材料費計", "人件費計", "委託費計", "設備関係費計"] },
];
const KPI_METRICS = MOBILE_GROUPS.flatMap((g) => g.metrics);

// 表示名 (長い 経費等 はモバイルでは短縮しつつ、 本部経費を隠さない)
const MOBILE_LABEL: Record<string, string> = { "経費等": KEIHI_LABEL.replace("経費等", "経費等 ") };

// 色 (PC 版の積み上げと同じ)
const METRIC_COLOR: Record<string, string> = {
  医業収益計: "#0a2463", 入院収入: "#1e4a8f", 外来収入: "#4a7ab5", 保険等査定減: "#94a3b8",
  経常利益: "#ff1493", 医業利益: "#ff69b4", 医業外損益: "#4b9cd3", 経費等: "#9b59b6",
  材料費計: "#d2691e", 人件費計: "#dc143c", 委託費計: "#8b4513", 設備関係費計: "#008080",
};
// 「増えるのが良い」か。 費用だけ false。
// ★保険等査定減 は DB 上 **負値** で「0 に近づく = 査定が減る = 良い」 → 増が良い側。
const HIGHER_IS_BETTER = (m: string): boolean =>
  !["材料費計", "人件費計", "委託費計", "設備関係費計", "経費等"].includes(m);

export function PlMobile() {
  const { data: raw, isLoading, error } = useQuery<PlResponse>({
    queryKey: ["pl-mobile"],
    queryFn: api.pl,
    staleTime: 5 * 60 * 1000,
  });
  // [2026-09-11] PC 版と同じ派生 (経費等 の作り直し + 医業外損益 の追加) を通す。
  const data = useMemo(() => raw ? derivePlSeries(raw) : raw, [raw]);

  const [targetMonth, setTargetMonth] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // data 読み込み後に targetMonth を最新月に同期 (初期化)
  useEffect(() => {
    if (data && targetMonth == null) setTargetMonth(data.dateMax);
  }, [data, targetMonth]);

  if (isLoading || !data) {
    return (
      <MobileShell title="📊 PL 速報" prototypeId="A" prototypeName="PL" >
        <div className="text-sm text-slate-400">{error ? `error: ${(error as Error).message}` : "読み込み中…"}</div>
      </MobileShell>
    );
  }

  const tm = targetMonth ?? data.dateMax;
  // 月送り ←→: PL は monthly レスポンスに dateMin..dateMax の範囲があるので利用 (連続月想定)
  const shiftMonth = (delta: number): string => {
    const [y, m, d] = tm.split("-").map(Number);
    const next = new Date(Date.UTC(y, m - 1 + delta, d));
    return [next.getUTCFullYear(), String(next.getUTCMonth() + 1).padStart(2, "0"), String(next.getUTCDate()).padStart(2, "0")].join("-");
  };
  const prevMo = shiftMonth(-1);
  const nextMo = shiftMonth(+1);
  const canPrev = prevMo.slice(0, 7) >= data.dateMin.slice(0, 7);
  const canNext = nextMo.slice(0, 7) <= data.dateMax.slice(0, 7);

  return (
    <>
      <MobileShell
        title="📊 PL 速報"
        prototypeId="A"
        prototypeName="PL"
        asOfMonth={tm}
        onPrevMonth={() => setTargetMonth(prevMo)}
        onNextMonth={() => setTargetMonth(nextMo)}
        canPrevMonth={canPrev}
        canNextMonth={canNext}
        headerRight={
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="text-slate-500 hover:text-slate-800 active:bg-slate-100 rounded p-1.5 text-base leading-none"
            title="対象月切替"
            aria-label="フィルタ"
          >⚙</button>
        }
      >
        <div className="flex items-center gap-1.5 text-[10px] text-slate-500 flex-wrap">
          <span className="bg-slate-100 px-1.5 py-0.5 rounded">基準月 {data.baseMonth.slice(0, 7)} = 100</span>
          <span className="bg-slate-100 px-1.5 py-0.5 rounded">単位: 百万円</span>
          {tm !== data.dateMax && (
            <button
              onClick={() => setTargetMonth(data.dateMax)}
              className="bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded border border-amber-200 active:bg-amber-200"
            >× 最新月へ</button>
          )}
        </div>

        <PlKpis data={data} targetMonth={tm} />
        <PlMaterialBreakdown data={data} targetMonth={tm} />

        <p className="text-[10px] text-slate-400 text-center px-4 leading-relaxed">
          詳細 (年計推移 / 月次積み上げ棒) は PC 版へ。
        </p>
      </MobileShell>

      <PlDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        data={data}
        targetMonth={tm}
        setTargetMonth={setTargetMonth}
      />
    </>
  );
}

function PlDrawer({ open, onClose, data, targetMonth, setTargetMonth }: {
  open: boolean; onClose: () => void; data: PlResponse;
  targetMonth: string; setTargetMonth: (v: string) => void;
}) {
  // monthly に存在する全月リスト (降順、 最新が先頭)
  const monthOptions = useMemo(() => {
    const s = new Set<string>();
    data.monthly.forEach((r) => s.add(r.date));
    return [...s].sort().reverse();
  }, [data.monthly]);

  // body scroll lock
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  return (
    <>
      <div
        onClick={onClose}
        className={`fixed inset-0 bg-slate-900 z-40 transition-opacity duration-200 ${
          open ? "opacity-40 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        aria-hidden={!open}
      />
      <aside
        className={`fixed top-0 right-0 bottom-0 w-[85%] max-w-sm bg-white z-50 shadow-2xl flex flex-col transition-transform duration-200 ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
        role="dialog"
        aria-modal="true"
        aria-hidden={!open}
      >
        <header className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-800">⚙ フィルタ</h2>
          <button onClick={onClose} className="text-slate-400 text-xl" aria-label="閉じる">×</button>
        </header>
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4 text-sm">
          <div>
            <div className="text-xs font-medium text-slate-700 mb-1.5">対象月</div>
            <select
              value={targetMonth}
              onChange={(e) => setTargetMonth(e.target.value)}
              className="w-full border border-slate-300 rounded px-2 py-1.5 bg-white"
            >
              {monthOptions.map((m) => (
                <option key={m} value={m}>{m.slice(0, 7)}{m === data.dateMax ? " (最新)" : ""}</option>
              ))}
            </select>
            <div className="text-[10px] text-slate-400 mt-1">
              月次値・年計値・指数の集計時点を切替えます。
            </div>
          </div>
        </div>
        <footer className="px-4 py-3 border-t border-slate-200 flex gap-2">
          <button
            onClick={() => setTargetMonth(data.dateMax)}
            className="flex-1 text-xs py-2 border border-slate-300 rounded text-slate-600 active:bg-slate-100"
          >最新月に戻す</button>
          <button
            onClick={onClose}
            className="flex-1 text-xs py-2 bg-slate-900 text-white rounded font-semibold active:bg-slate-800"
          >適用して閉じる</button>
        </footer>
      </aside>
    </>
  );
}

function PlKpis({ data, targetMonth }: { data: PlResponse; targetMonth: string }) {
  // targetMonth の monthly 値
  const monthlyByMetric = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of data.monthly) if (r.date === targetMonth) m.set(r.metric, r.value);
    return m;
  }, [data.monthly, targetMonth]);

  // targetMonth の rolling12 (年計)。 rolling12 が targetMonth に無ければ前月にフォールバック
  const rolling12ByMetric = useMemo(() => {
    const m = new Map<string, number>();
    const rollingDates = [...new Set(data.rolling12.map((r) => r.date))].sort();
    // targetMonth <= 各日付 のうち最も大きい (= 当該月かそれ以前で最新の rolling12)
    const matched = [...rollingDates].reverse().find((d) => d <= targetMonth) ?? rollingDates[rollingDates.length - 1];
    if (matched) {
      for (const r of data.rolling12) if (r.date === matched) m.set(r.metric, r.value);
    }
    return m;
  }, [data.rolling12, targetMonth]);

  // 指数 (基準月=100) の trend (sparkline) — targetMonth 以前の時系列に絞る。
  //
  // ★`data.indices` は使わない。 あれは R が作った表で、
  //   (a) 経費等 が旧式のまま、 (b) 医業外損益 の行が無い。
  //   ここでは **作り直した rolling12 から自分で指数化する** ので、 PC 版と定義が揃う。
  //   基準月の値が 0 以下 / 非有限の指標は指数が意味を持たない (符号が反転する) ので null。
  //   これは R 側 calculate_indices と同じ規則 (pl_analysis.R の problematic 判定)。
  //   → 経常利益 / 医業利益 / 医業外損益 / 保険等査定減 は基準月次第で指数が出ない。
  const indicesTrend = useMemo(() => {
    const byMetric = new Map<string, { date: string; value: number }[]>();
    for (const r of data.rolling12) {
      if (r.date > targetMonth) continue;
      if (!KPI_METRICS.includes(r.metric)) continue;
      if (!byMetric.has(r.metric)) byMetric.set(r.metric, []);
      byMetric.get(r.metric)!.push(r);
    }
    const m = new Map<string, (number | null)[]>();
    for (const [metric, pts] of byMetric.entries()) {
      pts.sort((a, b) => a.date.localeCompare(b.date));
      const base = data.rolling12.find((r) => r.date === data.baseMonth && r.metric === metric)?.value;
      const usable = base != null && Number.isFinite(base) && base > 0;
      m.set(metric, pts.map((p) => usable ? (p.value / (base as number)) * 100 : null));
    }
    return m;
  }, [data.rolling12, data.baseMonth, targetMonth]);

  // targetMonth 時点の指数値 (基準月比 +X%)。 trend 末尾 = targetMonth or それ以前の最新
  const latestIndexByMetric = useMemo(() => {
    const m = new Map<string, number | null>();
    for (const metric of KPI_METRICS) {
      const arr = indicesTrend.get(metric);
      if (!arr || arr.length === 0) { m.set(metric, null); continue; }
      let last: number | null = null;
      for (let i = arr.length - 1; i >= 0; i--) { if (arr[i] != null) { last = arr[i]; break; } }
      m.set(metric, last);
    }
    return m;
  }, [indicesTrend]);


  // [2026-09-11] PC 版と同じ 収益 / 利益 / 費用 の 3 群 × 4 = 12 枚。
  //   スマホなので 2 列 × 6 行に流し、 群ごとに小見出しを挟む。
  return (
    <section className="space-y-2">
      <div className="text-xs text-slate-500">{targetMonth.slice(0, 4)}年{targetMonth.slice(5, 7)}月 主要指標 (月次 + 年計)</div>
      {MOBILE_GROUPS.map((group) => (
      <div key={group.label}>
      <div className="text-[10px] font-semibold text-slate-500 mb-1">{group.label}</div>
      <div className="grid grid-cols-2 gap-2">
        {group.metrics.map((metric) => {
          const monthlyVal = monthlyByMetric.get(metric);
          const rollingVal = rolling12ByMetric.get(metric);
          const indexVal = latestIndexByMetric.get(metric);
          const trend = indicesTrend.get(metric) ?? [];
          const color = METRIC_COLOR[metric] ?? "#64748b";
          const indexDelta = indexVal != null ? indexVal - 100 : null;
          // 増えるのが良い指標は indexDelta>0 が青、 費用は indexDelta<0 が青
          const isGood = indexDelta == null ? null
                       : HIGHER_IS_BETTER(metric) ? indexDelta > 0 : indexDelta < 0;
          const deltaColor = isGood == null ? "text-slate-500"
                           : isGood ? "text-blue-700" : "text-red-700";
          return (
            <div key={metric} className="bg-white border border-slate-200 rounded-lg p-2.5">
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ background: color }} />
                <div className="text-[11px] text-slate-700 font-medium leading-tight">{MOBILE_LABEL[metric] ?? metric}</div>
              </div>
              <div className="mt-1">
                <div className="text-xl font-bold text-slate-900 tabular-nums leading-none">
                  {monthlyVal != null ? fmtMillion(monthlyVal) : "—"}
                  <span className="text-[10px] text-slate-400 ml-1 font-normal">百万</span>
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5">月次</div>
              </div>
              <div className="mt-2 pt-2 border-t border-slate-100">
                <div className="text-sm font-semibold text-slate-700 tabular-nums leading-none">
                  {rollingVal != null ? fmtMillion(rollingVal) : "—"}
                  <span className="text-[9px] text-slate-400 ml-1 font-normal">百万 (年計)</span>
                </div>
                {indexDelta != null && (
                  <div className={`text-[10px] font-semibold ${deltaColor} mt-0.5 tabular-nums`}>
                    指数 {indexVal!.toFixed(1)} ({indexDelta >= 0 ? "+" : ""}{indexDelta.toFixed(1)})
                  </div>
                )}
              </div>
              {trend.length > 1 && trend.some((v) => v != null) && (
                <div className="mt-1 -mx-1">
                  <Sparkline values={trend} width={140} height={20} stroke={color} showLast />
                </div>
              )}
            </div>
          );
        })}
      </div>
      </div>
      ))}
      {/* ★指数が出ない指標がある。 基準月の年計が 0 以下だと指数は符号が反転して意味を失うので
          出さない (経常利益 / 医業利益 / 医業外損益 / 保険等査定減 が該当しうる)。 R 側 calculate_indices と同じ規則。 */}
      <p className="text-[10px] text-slate-400 leading-relaxed">
        指数は基準月の年計を 100 とした比。基準月の年計が 0 以下の指標 (赤字の利益・医業外損益・査定減) は
        比が意味を持たないので出していない。
      </p>
    </section>
  );
}

function PlMaterialBreakdown({ data, targetMonth }: { data: PlResponse; targetMonth: string }) {
  // is_material=true の metric を rolling12 から targetMonth (以前で最新) の値で抽出
  const items = useMemo(() => {
    const materialMetrics = data.metrics.filter((r) => r.is_material).map((r) => r.metric);
    const rollingDates = [...new Set(data.rolling12.map((r) => r.date))].sort();
    const matched = [...rollingDates].reverse().find((d) => d <= targetMonth) ?? rollingDates[rollingDates.length - 1];
    if (!matched) return [];
    return materialMetrics.map((metric) => {
      const meta = data.metrics.find((m) => m.metric === metric)!;
      const pt = data.rolling12.find((r) => r.date === matched && r.metric === metric);
      const ix = data.indices.find((r) => r.date === matched && r.metric === metric);
      return {
        metric,
        color: meta.color,
        rolling: pt?.value ?? null,
        index: ix?.value ?? null,
      };
    }).sort((a, b) => (b.rolling ?? 0) - (a.rolling ?? 0));
  }, [data, targetMonth]);

  if (items.length === 0) return null;

  return (
    <section>
      <h2 className="text-xs font-semibold text-slate-700 mb-1">材料費 内訳 (年計、 百万円)</h2>
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="grid grid-cols-[1fr_auto_3.5rem] gap-2 px-3 py-2 bg-slate-100 text-[10px] font-semibold text-slate-600 border-b">
          <div>項目</div>
          <div className="text-right">年計</div>
          <div className="text-right">指数</div>
        </div>
        {items.map((it) => {
          const delta = it.index != null ? it.index - 100 : null;
          // 材料費は減るのが良い → delta<0 が青
          const deltaColor = delta == null ? "text-slate-400"
                           : delta < 0 ? "text-blue-700" : "text-red-700";
          return (
            <div key={it.metric} className="grid grid-cols-[1fr_auto_3.5rem] gap-2 px-3 py-1.5 text-xs border-b border-slate-100 last:border-b-0 items-center">
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: it.color }} />
                <span className="text-slate-800">{it.metric}</span>
              </div>
              <div className="text-right tabular-nums text-slate-700">{it.rolling != null ? fmtInt(toMillion(it.rolling)) : "—"}</div>
              <div className={`text-right tabular-nums font-semibold ${deltaColor}`}>
                {it.index != null ? it.index.toFixed(1) : "—"}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
